import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ExternalLink, ArrowRight } from 'lucide-react';
import { useProducts } from '../hooks/useAPI';

const ProductsSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { data: products, isLoading, error } = useProducts();

  // Default products if API fails
  const defaultProducts = [
    {
      id: 1,
      title: 'EcoTracker',
      description: 'A sophisticated sustainability tracking platform that helps businesses monitor and reduce their environmental footprint.',
      demoUrl: '#',
      features: ['Real-time monitoring', 'Carbon footprint analysis', 'Compliance reporting', 'Smart recommendations']
    },
    {
      id: 2,
      title: 'DataGuard',
      description: 'Advanced cybersecurity suite providing comprehensive protection against modern digital threats.',
      demoUrl: '#',
      features: ['Threat detection', 'Real-time monitoring', 'Automated response', 'Security analytics']
    },
    {
      id: 3,
      title: 'SmartHR',
      description: 'Intelligent human resources management system that streamlines recruitment, onboarding, and employee lifecycle.',
      demoUrl: '#',
      features: ['Automated recruitment', 'Performance tracking', 'Employee analytics', 'Payroll integration']
    }
  ];

  const displayProducts = products && products.length > 0 ? products : defaultProducts;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <section id="products" className="py-20 md:py-24 bg-lion-gray">
      <div className="container mx-auto px-6" ref={ref}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.span 
            variants={itemVariants}
            className="font-bold text-lion-yellow text-sm uppercase tracking-wider"
          >
            OUR PRODUCTS
          </motion.span>
          
          <motion.h2 
            variants={itemVariants}
            className="text-3xl md:text-4xl font-poppins font-bold text-lion-blue mt-2 mb-4"
          >
            Innovative Solutions We've Built
          </motion.h2>
          
          <motion.p 
            variants={itemVariants}
            className="max-w-2xl mx-auto text-gray-600"
          >
            Explore our portfolio of cutting-edge products that showcase our expertise and innovation.
          </motion.p>
        </motion.div>

        {isLoading ? (
          <div className="flex justify-center">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {displayProducts.map((product, index) => (
              <motion.div
                key={product.id || index}
                variants={cardVariants}
                custom={index}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group"
              >
                {/* Product Image Placeholder */}
                <div className="h-48 bg-gradient-to-br from-lion-blue to-lion-yellow flex items-center justify-center">
                  <h3 className="text-2xl font-poppins font-bold text-white">
                    {product.title}
                  </h3>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-poppins font-bold text-lion-blue mb-3 group-hover:text-lion-yellow transition-colors duration-300">
                    {product.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {product.description}
                  </p>
                  
                  {/* Features */}
                  {product.features && product.features.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-lion-blue mb-2">Key Features:</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {product.features.slice(0, 4).map((feature, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="w-1.5 h-1.5 bg-lion-yellow rounded-full mt-2 mr-2 flex-shrink-0"></span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Demo Button */}
                  {product.demoUrl && (
                    <a
                      href={product.demoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-lion-blue hover:text-lion-yellow transition-colors duration-300 font-medium group"
                    >
                      <span>View Demo</span>
                      <ExternalLink className="w-4 h-4 ml-2 group-hover:transform group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
                    </a>
                  )}
                  
                  {!product.demoUrl && (
                    <div className="inline-flex items-center text-lion-blue hover:text-lion-yellow transition-colors duration-300 font-medium cursor-pointer group">
                      <span>Learn More</span>
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:transform group-hover:translate-x-1 transition-all duration-300" />
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {error && (
          <motion.p 
            variants={itemVariants}
            className="text-gray-500 text-center mt-8"
          >
            Products will be loaded shortly. Please check back soon.
          </motion.p>
        )}
      </div>
    </section>
  );
};

export default ProductsSection;